import CvvPresenter from "./CvvPresenter";
import {
  CommonReportResponse,
  CvvCheckNowRequest,
  CvvCheckNowRequestPartial,
  CvvCheckNowResponse,
  CVVPairItem,
  CvvQueryString,
  CvvViewListItem,
  CvvViewResponse,
  DayNightItem,
  ReportDataRow,
} from "api/interfaces/reportInterface.interface";
import { Option } from "utils/options";
import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  getCvvRecorderList,
  getCvvView,
  getReportInfoCVV,
  postCVVCheckNow,
  putCVVReferenceUpdate,
  putCVVReferenceUpdateAll,
} from "api/reportAPI";
import { useMutation } from "react-query";
import { useLocation, useParams } from "react-router-dom";
import useStatusMutation from "hook/useStatusMutation";
import NoAvailable from "components/templates/nopage/NoAvailable";
import { useMenu } from "hook/useMenu";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import useApiError from "hook/useApiError";
import { CustomError } from "api/interfaces/commonInterface.interface";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { checkEmpty } from "utils/functions";

type Props = {
  state: ReportDataRow | undefined;
};

export enum CvvBase {
  REFERENCE,
  EVENT,
}

const initialCvvListItem = {
  isWarning: false,
  date: "",
  usageCameras: 0,
  totalCameras: 0,
  warningCameras: 0,
  cvvCameras: 0,
  recorderType: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  offset: new Date().getTimezoneOffset(),
  location: {
    id: 0,
    location: "",
    latitude: 0,
    longitude: 0,
  },
  cameras: [],
};

const hasListAnalyzingThumbnail = (
  cvvListRefetched: CvvViewListItem
): boolean => {
  return cvvListRefetched.cameras.some((camera) => {
    return (
      camera.day.eventThumbnail.isAnalyzing ||
      camera.night.eventThumbnail.isAnalyzing
    );
  });
};

export default function CvvContainer(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { menuNavigation, queryNavigate } = useMenu({});
  const params = useParams();

  const [cvvList, setCvvList] = useState<CvvViewListItem>(initialCvvListItem);

  const reportId = params.reportId as string;
  const [reportTitle, setReportTitle] = useState<string>("");
  const [hasAnalyzingThumbnail, setHasAnalyzingThumbnail] =
    useState<boolean>(false);
  const { lang, notifyError, notifySuccess, handleErrorNotify } = useApiError();
  const referErrorMsg = lang.label.report.notify.cvv.update["90005"];
  // const titleTemp =
  // location.state !== null
  //   ? location.state.title
  //   : props.state
  //   ? props.state.title
  //   : "";
  // const idTemp =
  // location.state !== null
  //   ? location.state.id
  //   : props.state
  //   ? props.state.id
  //   : "";
  // const [id, setId] = useState<string | undefined>(idTemp);
  // const [title, setTitle] = useState<string | undefined>(titleTemp);

  // useEffect(()=>{
  //   if(location.state){
  //     if(location.state.id){
  //       setId(location.state.id);
  //     }
  //     if(location.state.title){
  //       setTitle(location.state.title);
  //     }
  //   }
  // },[location])
  const reportCvvDetailInfo = useQuery(
    ["reportCvvDetailInfo", reportId, selectedAccount.accountId],
    () =>
      getReportInfoCVV({
        accountId: selectedAccount.accountId,
        reportId: reportId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: selectedAccount.accountId !== "",
      onSuccess: (res: any) => {
        if (res.result !== undefined && res.error === 0) {
          setReportTitle(res.result.preferences?.title);
        } else {
          setReportTitle("N/A");
        }
      },
      onError: (e: any) => {
        setReportTitle("N/A");
      },
    }
  );

  const [recorderList, setRecorderList] = useState<Option[]>([]);
  const [selectFilterRecorder, setSelectFilterRecorder] = useState<string>("");

  const requestRecorderList = useQuery(
    ["requestRecorderList", selectedAccount.accountId, reportId],
    () =>
      getCvvRecorderList({
        accountId: selectedAccount.accountId,
        reportId: reportId as string,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: reportId !== undefined && selectedAccount.accountId !== "",
      onSuccess: (res: any) => {
        if (
          res.error !== 0 ||
          res.result === undefined ||
          checkEmpty(res.result)
        ) {
          setRecorderList([]);
          return;
        }
        setRecorderList(
          (res.result || [])
            .sort((a: any, b: any) => {
              if (a.mergedSystemName === b.mergedSystemName) {
                return a.name.localeCompare(b.name);
              }
              return a.mergedSystemName.localeCompare(b.mergedSystemName);
            })
            .map((data: any, index: number, _self: any[]) => {
              return {
                label:
                  data.mergedSystemName === data.name ||
                  _self.findIndex(
                    (item: any, idx: number) =>
                      item.mergedSystemId === data.mergedSystemId &&
                      index !== idx
                  ) === -1
                    ? data.mergedSystemName
                    : `[${data.mergedSystemName}] ${data.name}`,
                value: data.id,
              };
            })
        );
        const changeParam = new URLSearchParams(location.search);
        if (
          changeParam.get("recorderId") &&
          res.result.find(
            (data: any) => data.id === changeParam.get("recorderId")
          )
        ) {
          setSelectFilterRecorder(changeParam.get("recorderId") as string);
        } else {
          let queryInfo = {
            ...cvvQueryInfo,
            recorderId: res.result[0].id as string,
          };
          menuNavigation({
            to: location.pathname,
            queryString: {
              ...queryInfo,
            },
            replace: true,
          });
        }
      },
      onError: (e: any) => {
        setRecorderList([]);
      },
    }
  );

  const [cvvQueryInfo, setCvvQueryInfo] = useState<CvvQueryString>({
    ...baseQueryInfo,
    pageLimit: 10,
    cvvEnabled: false,
    recorderId: selectFilterRecorder,
    sortType: "",
  });
  const cvvQueryKey = useQueryKey(cvvQueryInfo);

  const [listCount, setListCount] = useState<number>(0);

  const pollUntilAnalyzeDone = useQuery(
    ["cvvList", cvvQueryKey, reportId],
    () =>
      getCvvView({
        reportId: reportId as string,
        payload: cvvQueryInfo,
      }),
    {
      refetchInterval: 3000,
      enabled: hasAnalyzingThumbnail,
      onSuccess: (res: CvvViewResponse) => {
        if (!res.result || checkEmpty(res.result)) {
          return;
        }

        if (hasListAnalyzingThumbnail(res.result)) {
          setHasAnalyzingThumbnail(true);
          return;
        }
        setHasAnalyzingThumbnail(false);
        setListCount(res.page.total);
        setCvvList(res.result);
      },
      onError: (err: any) => {},
    }
  );

  const { refetch: refetchCvvList } = useQuery(
    ["cvvList", cvvQueryKey, reportId],
    () =>
      getCvvView({
        reportId: reportId as string,
        payload: cvvQueryInfo,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: selectFilterRecorder !== "" && reportId !== undefined,
      onSuccess: (res: CvvViewResponse) => {
        // console.log(res.result);
        if (
          res.error !== 0 ||
          res.result === undefined ||
          checkEmpty(res.result)
        ) {
          setListCount(0);
          // setCvvQueryInfo((info) => {
          //   return { ...info, total: 0 as number, pageNum: 0 };
          // });
          setCvvList(initialCvvListItem);
        } else {
          setListCount(res.page.total);
          // setCvvQueryInfo((info) => {
          //   return { ...info, total: res.page.total as number };
          // });
          setCvvList(res.result);
          if (hasListAnalyzingThumbnail(res.result)) {
            setHasAnalyzingThumbnail(true);
          }
        }
      },
      onError: (err: any) => {
        notifyError(lang.label.report.notify.cvv.list.error);
        setListCount(0);
        setCvvList(initialCvvListItem);
        // setCvvQueryInfo((info) => {
        //   return { ...info, total: 0 as number, pageNum: 0 };
        // });
      },
    }
  );

  const onChangeFilterRecorder = useCallback(
    (value: string) => {
      setSelectFilterRecorder(value);
      let queryInfo = { ...cvvQueryInfo, pageNum: 0, recorderId: value };
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          ...queryInfo,
        },
      });
    },
    [cvvQueryInfo, queryNavigate]
  );

  const onClickSearch = useCallback(
    (keyword: string) => {
      //if(cvvQueryInfo.keyword !== keyword){
      let queryInfo = { ...cvvQueryInfo, keyword: keyword, pageNum: 0 };
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          ...queryInfo,
        },
      });
      // }else{
      //   refetch();
      // }
      // //refetch();
    },
    [cvvQueryInfo, queryNavigate]
  );

  const onChangePage = useCallback(
    (page: number, totalRows: number) => {
      if (page !== cvvQueryInfo.pageNum + 1) {
        const screen = document.getElementById("inner-container");
        if (screen) {
          screen.scrollTo({ top: 0 });
        }
        window.scrollTo({ top: 0 });
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            ...cvvQueryInfo,
            pageNum: page - 1,
          },
        });
      }
    },
    [cvvQueryInfo, queryNavigate]
  );

  const onChangeIsCvv = useCallback(
    (checked: boolean) => {
      let queryInfo = {
        ...cvvQueryInfo,
        pageNum: 0,
        cvvEnabled: checked.toString(),
      };
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          ...queryInfo,
        },
      });
    },
    [cvvQueryInfo, queryNavigate]
  );

  const [showNotifyAutoUpdate, setShowNotifyAutoUpdate] =
    useState<boolean>(false);
  const onClickNotifyAutoUpdate = useCallback(() => {
    setShowNotifyAutoUpdate(false);
  }, []);
  const onSuccessReferenceUpdate = useCallback(
    (res: CommonReportResponse & { result: boolean }) => {
      if (res.error || res.errorString) {
        notifyError(res.errorString ? res.errorString : referErrorMsg);
        return;
      }
      notifySuccess(lang.label.report.notify.cvv.refer.camera.success);
      if (res.result) {
        setShowNotifyAutoUpdate(true);
      }
      refetchCvvList();
    },
    [referErrorMsg]
  );
  const onErrorReferenceUpdate = useCallback(
    (err: CustomError) => {
      if (err.response?.data.error === 90005) {
        notifyError(referErrorMsg);
        return;
      }
      handleErrorNotify(err, referErrorMsg);
    },
    [referErrorMsg]
  );
  const mutationReferenceUpdate = useMutation(putCVVReferenceUpdate, {
    onSuccess: onSuccessReferenceUpdate,
    onError: onErrorReferenceUpdate,
  });
  const onClickUpdate = useCallback(
    (data: DayNightItem, pairItem: CVVPairItem) => {
      return mutationReferenceUpdate.mutateAsync({
        reportId: reportId,
        payload: {
          recorderId: selectFilterRecorder,
          cameraId: data.cameraId,
          channel: data.channel,
          srcId: pairItem.referenceThumbnail.thumbnailId,
          dstId: pairItem.eventThumbnail.thumbnailId,
        },
      });
    },
    [mutationReferenceUpdate, reportId, selectFilterRecorder]
  );

  const mutationReferenceUpdateAll = useMutation(putCVVReferenceUpdateAll, {
    onSuccess: onSuccessReferenceUpdate,
    onError: onErrorReferenceUpdate,
  });
  const onClickUpdateAll = useCallback(() => {
    mutationReferenceUpdateAll.mutate({
      reportId: reportId,
      recorderId: selectFilterRecorder,
    });
  }, [mutationReferenceUpdateAll, reportId, selectFilterRecorder]);

  const mutationCheckNow = useStatusMutation(postCVVCheckNow, {
    onSuccess: (res: CvvCheckNowResponse) => {
      notifySuccess(lang.label.report.notify.cvv.checknow.success);
      refetchCvvList();
    },
    onError: (err: CustomError) => {
      if (err.response?.data.error === 90005) {
        notifyError(lang.label.report.notify.cvv.update["90005"]);
        return;
      }
      handleErrorNotify(err, lang.label.report.notify.cvv.refer.camera.fail);
    },
  });

  const onClickCheckNow = async (payload: CvvCheckNowRequestPartial) => {
    if (!mutationCheckNow.idle) return;
    const request: CvvCheckNowRequest = {
      ...payload,
      reportId: reportId as string,
    };
    await mutationCheckNow.mutateAsync(request).catch((err: any) => {});
  };
  const location = useLocation();

  const queryClient = useQueryClient();
  const refecth = useCallback(() => {
    reportCvvDetailInfo.refetch();
    queryClient.invalidateQueries(["storageStatus", selectedAccount.accountId]);
    requestRecorderList.refetch();
    pollUntilAnalyzeDone.refetch();
    refetchCvvList();
  }, []);
  useStompDebouncedRefetch({
    stompKey: ["reportDetailUpdated"] as StompDateSignalKey[],
    refetch: refecth,
  });
  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      if (changeParam.get("recorderId")) {
        setSelectFilterRecorder(
          (changeParam.get("recorderId") as string) || recorderList[0].value
        );
      }
      setCvvQueryInfo((info) => {
        return {
          ...info,
          keyword: changeParam.get("keyword")
            ? (changeParam.get("keyword") as string)
            : "",
          recorderId: changeParam.get("recorderId")
            ? (changeParam.get("recorderId") as string)
            : "",
          pageNum: changeParam.get("pageNum")
            ? Number(changeParam.get("pageNum"))
            : 0,
          cvvEnabled: changeParam.get("cvvEnabled")
            ? changeParam.get("cvvEnabled") === "true"
            : false,
        };
      });
    }
  }, [location.search]);

  if (reportCvvDetailInfo.error) {
    return (
      <NoAvailable
        onReturn={() => menuNavigation({ to: "/reports" })}
        buttonLabel="Return to Reports"
        message="This report is no longer available"
      />
    );
  }

  return (
    <CvvPresenter
      title={reportTitle}
      recorderList={recorderList}
      recorderListIsFetching={requestRecorderList.isFetching}
      cvvList={cvvList}
      selectFilterRecorder={selectFilterRecorder}
      onChangeFilterRecorder={onChangeFilterRecorder}
      onClickSearch={onClickSearch}
      cvvQueryInfo={cvvQueryInfo}
      onChangePage={onChangePage}
      selectedAccount={selectedAccount}
      onChangeIsCvv={onChangeIsCvv}
      onClickUpdate={onClickUpdate}
      onClickUpdateAll={onClickUpdateAll}
      onClickCheckNow={onClickCheckNow}
      listCount={listCount}
      disabledCheckNow={!mutationCheckNow.idle}
      showNotifyAutoUpdate={showNotifyAutoUpdate}
      onClickNotifyAutoUpdate={onClickNotifyAutoUpdate}
      // setIsCvvOpen={setIsCvvOpen}
    />
  );
}
